import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../../../../api';
import { AppSelect } from '../../../../../../components/common/select';

const COMPANY_SIZE_OPTIONS = [
  { id: '1', name: '1 - 5' }, { id: '2', name: '6 - 10' }, { id: '3', name: '11 - 15' }, { id: '4', name: '16 - 25' },
  { id: '5', name: '26 - 45' }, { id: '6', name: '46 - 65' }, { id: '7', name: '66 - 85' }, { id: '8', name: '86 - 125' },
  { id: '9', name: '126 - 175' }, { id: '10', name: '176 - 275' }, { id: '11', name: '276 - 425' }, { id: '12', name: '426 - 625' },
  { id: '13', name: '626 - 875' }, { id: '14', name: '876 - 1175' }, { id: '15', name: '1176 - 1550' }, { id: '16', name: '1551 - 2025' },
  { id: '17', name: '2026 - 2675' }, { id: '18', name: '2676 - 3450' }, { id: '19', name: '3451 - 4350' }, { id: '20', name: '4351 - 5450' },
  { id: '21', name: '5451 - 6800' }, { id: '22', name: '6801 - 8500' }, { id: '23', name: '8501 - 10700' }, { id: '24', name: '> 10700' }
];

const OverviewView = ({ clientData, contacts = [], onUpdateClient }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editKey, setEditKey] = useState(0);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Focus plumbing for a newly added contact row: the index is captured inside
  // the state updater (the only place the new length is known), and focusTick
  // fires the effect once React has rendered that row.
  const pendingFocusIndexRef = useRef(null);
  const contactNameInputRefs = useRef({});
  const [focusTick, setFocusTick] = useState(0);

  // Display values for read-only view
  const [displayTagging, setDisplayTagging] = useState('');
  const [displayCurrencyName, setDisplayCurrencyName] = useState('');

  // ─── Fetch dropdown options once on mount ────────────────────────────────────
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setOptionsLoading(true);
        const [partnerTypeRes, entityTypeRes, sectorRes, taggingRes, currencyRes] = await Promise.all([
          api.get('/md/business-partner-type'),
          api.get('/md/entity-type'),
          api.get('/md/sector'),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }),
          api.get('/crm/currencies')
        ]);
        setPartnerTypes(partnerTypeRes.data || []);
        setEntityTypes(entityTypeRes.data || []);
        setSectors(sectorRes.data || []);
        setTaggingOptions(taggingRes.data || []);
        setCurrencies(currencyRes.data || []);
      } catch (err) {
        console.error('Error fetching options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  // ─── Fetch company detail once to populate read-only display values ───────────
  useEffect(() => {
    if (!clientData?.companyId) return;
    const fetchForDisplay = async () => {
      try {
        const res = await api.get(`/md/companies/${clientData.companyId}`);
        const company = (res.data.company) || res.data;
        if (company.taggings && Array.isArray(company.taggings)) {
          setDisplayTagging(company.taggings.map(t => t.name).join(', '));
        } else {
          setDisplayTagging(company.tagging || '');
        }
      } catch {}
    };
    fetchForDisplay();
  }, [clientData?.companyId]);

  // ─── Stable event handlers ───────────────────────────────────────────────────
  // Originally to stop TomSelect re-initialising on every render (its effect depended
  // on onChange identity). AppSelect has no such effect, so these are now kept simply
  // to avoid re-rendering the whole form on each keystroke.
  const handleCompanyInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleCompanySelectChange = useCallback((name, value) => {
    if (name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const ids = arr.map(v => (v != null && v !== '') ? String(v) : null).filter(Boolean);
      setFormData(prev => ({ ...prev, [name]: ids }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!value || !prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  // ─── Enter edit mode ─────────────────────────────────────────────────────────
  const handleEdit = async () => {
    if (!clientData?.companyId) return;
    setErrors({});
    try {
      setIsLoading(true);
      const res = await api.get(`/md/companies/${clientData.companyId}`);
      const data = res.data;
      // Mirror Prospects/index.jsx fetchCompanyDetail exactly
      const companyData = data.company;
      companyData.contacts = data.contacts || [];

      // businessPartnerType
      if (companyData.businessPartnerType) {
        if (typeof companyData.businessPartnerType === 'object') {
          companyData.businessPartnerType = companyData.businessPartnerType.id || companyData.businessPartnerType;
        } else if (typeof companyData.businessPartnerType === 'string' && partnerTypes.length > 0) {
          const found = partnerTypes.find(pt =>
            pt.name === companyData.businessPartnerType || String(pt.id) === String(companyData.businessPartnerType)
          );
          if (found) companyData.businessPartnerType = String(found.id);
        }
      }
      companyData.businessPartnerType = companyData.businessPartnerType != null ? String(companyData.businessPartnerType) : '';

      // entityType
      if (companyData.entityType) {
        if (typeof companyData.entityType === 'object') {
          companyData.entityType = companyData.entityType.id || companyData.entityType;
        } else if (typeof companyData.entityType === 'string' && entityTypes.length > 0) {
          const found = entityTypes.find(et =>
            et.entityType === companyData.entityType || String(et.id) === String(companyData.entityType)
          );
          if (found) companyData.entityType = String(found.id);
        }
      }
      companyData.entityType = companyData.entityType != null ? String(companyData.entityType) : '';

      // sector
      if (companyData.sector) {
        if (typeof companyData.sector === 'object') {
          companyData.sector = companyData.sector.id || companyData.sector;
        } else if (typeof companyData.sector === 'string' && sectors.length > 0) {
          const found = sectors.find(s =>
            s.name === companyData.sector || String(s.id) === String(companyData.sector)
          );
          if (found) companyData.sector = String(found.id);
        }
      }
      companyData.sector = companyData.sector != null ? String(companyData.sector) : '';

      // companySize
      companyData.companySize = companyData.companySize != null ? String(companyData.companySize) : '';

      // taggings → taggingIds
      if (companyData.taggings && Array.isArray(companyData.taggings)) {
        companyData.taggingIds = companyData.taggings.map(t => String(t.id));
      } else if (companyData.tagging && typeof companyData.tagging === 'string' && taggingOptions.length > 0) {
        const names = companyData.tagging.split(',').map(s => s.trim()).filter(Boolean);
        companyData.taggingIds = names.map(n => {
          const found = taggingOptions.find(opt => opt.name === n || String(opt.id) === n);
          return found ? String(found.id) : null;
        }).filter(Boolean);
      } else {
        companyData.taggingIds = [];
      }
      delete companyData.clientFlagging;
      delete companyData.clientFlaggingId;
      delete companyData.taggings;

      // Commit all state changes together so the selects mount with correct values
      setFormData(companyData);
      setEditKey(prev => prev + 1);
      setIsEditing(true);
      return true;
    } catch (err) {
      console.error('Error fetching company for edit:', err);
      setErrors({ general: 'Failed to load company data for editing.' });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Contact editing ─────────────────────────────────────────────────────────
  // PUT /md/companies/{id} treats `contacts` as the complete set: rows are
  // matched by id (falling back to email), and any existing contact absent from
  // the array is DELETED. So the array must always carry every contact we want
  // to keep — which is why handleEdit seeds formData.contacts from the GET.
  const handleContactChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const contacts = [...(prev.contacts || [])];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...prev, contacts };
    });
    setErrors(prev => {
      if (!prev.contacts?.[index]?.[field]) return prev;
      const nextContacts = { ...prev.contacts };
      const row = { ...nextContacts[index] };
      delete row[field];
      if (Object.keys(row).length === 0) delete nextContacts[index];
      else nextContacts[index] = row;
      const next = { ...prev };
      if (Object.keys(nextContacts).length === 0) delete next.contacts;
      else next.contacts = nextContacts;
      return next;
    });
  }, []);

  // Appends a blank contact row. Callable from read mode: it opens the edit
  // session first, so adding a contact is one click rather than Edit-then-Add.
  const handleAddContact = async () => {
    if (!isEditing) {
      const started = await handleEdit();
      if (!started) return;
    }
    setFormData(prev => {
      const contacts = [
        ...(prev.contacts || []),
        // __isNew is client-only: it drives the "New" badge and is dropped by
        // the payload mapper, which picks fields explicitly.
        { firstName: '', lastName: '', email: '', phone: '', position: '', __isNew: true },
      ];
      pendingFocusIndexRef.current = contacts.length - 1;
      return { ...prev, contacts };
    });
    setFocusTick(tick => tick + 1);
  };

  const handleRemoveContact = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      contacts: (prev.contacts || []).filter((_, i) => i !== index),
    }));
    setErrors(prev => {
      if (!prev.contacts) return prev;
      const next = { ...prev };
      delete next.contacts;
      return next;
    });
  }, []);

  useEffect(() => {
    if (focusTick === 0) return;
    const index = pendingFocusIndexRef.current;
    if (index == null) return;
    const input = contactNameInputRefs.current[index];
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    pendingFocusIndexRef.current = null;
  }, [focusTick]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.companyName?.trim()) newErrors.companyName = 'Company name is required';

    // Contact rules mirror the backend's own: email is required and must be
    // unique per company, otherwise the whole update is rejected.
    const contactErrors = {};
    const seenEmails = new Map();
    (formData.contacts || []).forEach((contact, index) => {
      const rowErrors = {};
      if (!contact.firstName?.trim()) rowErrors.firstName = 'First name is required';
      const email = contact.email?.trim();
      if (!email) {
        rowErrors.email = 'Email is required';
      } else {
        const normalized = email.toLowerCase();
        if (seenEmails.has(normalized)) {
          rowErrors.email = 'Duplicate email in this company';
        } else {
          seenEmails.set(normalized, index);
        }
      }
      if (Object.keys(rowErrors).length > 0) contactErrors[index] = rowErrors;
    });
    if (Object.keys(contactErrors).length > 0) {
      newErrors.contacts = contactErrors;
      newErrors.general = 'Please fix the highlighted contact fields.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!clientData?.companyId) return;
    setIsLoading(true);
    try {
      const taggingIdsAsNumbers = Array.isArray(formData.taggingIds)
        ? formData.taggingIds.map(id => Number(id)).filter(n => !isNaN(n))
        : [];

      const payload = {
        companyName: formData.companyName,
        businessPartnerType: formData.businessPartnerType,
        entityType: formData.entityType,
        sector: formData.sector,
        companySize: formData.companySize,
        website: formData.website || null,
        companyEmail: formData.companyEmail || null,
        phone: formData.phone || null,
        npwp: formData.npwp || null,
        taggingIds: taggingIdsAsNumbers,
        address: formData.address || null,
        postalCode: formData.postalCode || null,
        country: formData.country || null,
        state: formData.state || null,
        city: formData.city || null,
        // Only the fields md.ContactDTO declares — an unknown property would be
        // dropped anyway, and `id` is what lets the backend update a contact in
        // place (preserving its link to any Lead) instead of recreating it.
        contacts: (formData.contacts || []).map(contact => ({
          id: contact.id ?? null,
          firstName: contact.firstName || null,
          lastName: contact.lastName || null,
          email: contact.email?.trim() || null,
          phone: contact.phone || null,
          position: contact.position || null,
          title: contact.title || null,
          address: contact.address || null,
          city: contact.city || null,
          state: contact.state || null,
          postalCode: contact.postalCode || null,
          partnerType: contact.partnerType || null,
          businessPartnerType: contact.businessPartnerType || null,
        }))
      };
      await api.put(`/md/companies/${clientData.companyId}`, payload);

      // Update read-only display from saved formData (no extra API call needed)
      const taggingNames = (formData.taggingIds || []).map(id => {
        const opt = taggingOptions.find(o => String(o.id) === String(id));
        return opt ? opt.name : id;
      }).join(', ');
      setDisplayTagging(taggingNames);
      setIsEditing(false);
      setErrors({});
      if (onUpdateClient) await onUpdateClient();
    } catch (err) {
      console.error('Error saving client data:', err);
      setErrors({ general: err?.response?.data?.message || 'Failed to save changes. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
  };

  if (!clientData) {
    return <div>Loading...</div>;
  }

  const getInitials = (name) => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const getCompanySizeName = (sizeId) => {
    const opt = COMPANY_SIZE_OPTIONS.find(o => String(o.id) === String(sizeId));
    return opt ? opt.name : (sizeId != null && sizeId !== '' ? String(sizeId) : 'N/A');
  };

  const initials = getInitials(clientData.companyName);
  const editableContacts = formData.contacts || [];

  // Edit / Save / Cancel live in the page header (top right), not inside the
  // Basic information card — they govern the whole detail view, contacts included.
  const renderEditActions = () => (
    <div className="btn-list">
          {!isEditing ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEdit}
              disabled={optionsLoading || isLoading}
            >
              <svg className="icon icon-tabler icon-tabler-edit" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                <path d="M16 5l3 3"></path>
              </svg>
              Edit
            </button>
          ) : (
            <>
              <button className="btn btn-outline-secondary me-2" onClick={handleCancel} disabled={isLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="icon icon-tabler icon-tabler-device-floppy" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2"></path>
                      <circle cx="12" cy="14" r="2"></circle>
                      <polyline points="14,4 14,8 8,8 8,4"></polyline>
                    </svg>
                    Save
                  </>
                )}
              </button>
            </>
          )}
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="row row-cards">
        <div className="col-lg-12">

          {/* Header card — carries the Edit / Save / Cancel actions for the
              whole detail view (company fields + contacts) */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                <div className="me-3">
                  <span className="avatar avatar-xl text-white" style={{ backgroundColor: '#206bc4' }}>
                    {initials}
                  </span>
                </div>
                <div>
                  <h2 className="mb-0">{clientData.companyName}</h2>
                  <div className="text-secondary">{clientData.partnerType}</div>
                </div>
                <div className="ms-auto">{renderEditActions()}</div>
              </div>

              {errors.general && (
                <div className="alert alert-danger mb-0">{errors.general}</div>
              )}
            </div>
          </div>

          {/* Basic information card */}
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="card-title">Basic information</h3>
            </div>

            <div className="card-body">
              <div className="datagrid">

                {/* Company Name */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Company Name</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <div>
                        <input
                          type="text"
                          className={`form-control ${errors.companyName ? 'is-invalid' : ''}`}
                          name="companyName"
                          value={formData.companyName || ''}
                          onChange={handleCompanyInputChange}
                          placeholder="Enter company name"
                          autoComplete="organization"
                        />
                        {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
                      </div>
                    ) : clientData.companyName}
                  </div>
                </div>

                {/* Business Partner Type */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Business Partner Type</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <AppSelect
                        key={`businessPartnerType-${editKey}`}
                        name="businessPartnerType"
                        value={formData.businessPartnerType ?? ''}
                        onChange={handleCompanySelectChange}
                        options={partnerTypes}
                        placeholder="Select partner type"
                        label=""
                        error={errors.businessPartnerType}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                      />
                    ) : (clientData.partnerType || clientData.businessPartnerType || 'N/A')}
                  </div>
                </div>

                {/* Entity Type */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Entity Type</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <AppSelect
                        key={`entityType-${editKey}`}
                        name="entityType"
                        value={formData.entityType ?? ''}
                        onChange={handleCompanySelectChange}
                        options={entityTypes}
                        placeholder="Select entity type"
                        label=""
                        error={errors.entityType}
                        valueField="id"
                        labelField="entityType"
                        searchField="entityType"
                      />
                    ) : (clientData.entityType || 'N/A')}
                  </div>
                </div>

                {/* Sector */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Sector</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <AppSelect
                        key={`sector-${editKey}`}
                        name="sector"
                        value={formData.sector ?? ''}
                        onChange={handleCompanySelectChange}
                        options={sectors}
                        placeholder="Select sector"
                        label=""
                        error={errors.sector}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                      />
                    ) : (clientData.sector || 'N/A')}
                  </div>
                </div>

                {/* Company Size */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Company Size</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <AppSelect
                        key={`companySize-${editKey}`}
                        name="companySize"
                        value={formData.companySize ?? ''}
                        onChange={handleCompanySelectChange}
                        options={COMPANY_SIZE_OPTIONS}
                        placeholder="Select company size"
                        label=""
                        error={errors.companySize}
                      />
                    ) : getCompanySizeName(clientData?.companySize)}
                  </div>
                </div>

                {/* Website */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Website</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                        name="website"
                        value={formData.website || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter website"
                        inputMode="url"
                        autoComplete="url"
                      />
                    ) : (
                      clientData.website
                        ? <a href={clientData.website} target="_blank" rel="noopener noreferrer">{clientData.website}</a>
                        : 'N/A'
                    )}
                  </div>
                </div>

                {/* Company Email */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Company Email</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="email"
                        className="form-control"
                        name="companyEmail"
                        value={formData.companyEmail || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter company email"
                        autoComplete="email"
                      />
                    ) : (clientData.companyEmail || clientData.email || 'N/A')}
                  </div>
                </div>

                {/* Phone */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Phone</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter phone number"
                        inputMode="tel"
                        autoComplete="tel"
                      />
                    ) : (clientData.companyPhoneNumber || clientData.phone || 'N/A')}
                  </div>
                </div>

                {/* Company Tax Number / NPWP */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Company Tax Number/NPWP</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-control"
                        name="npwp"
                        value={formData.npwp || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter company tax number/NPWP"
                        autoComplete="off"
                      />
                    ) : (clientData.npwp || 'N/A')}
                  </div>
                </div>

                {/* Tagging */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Tagging</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <AppSelect
                        key={`taggingIds-${editKey}`}
                        name="taggingIds"
                        value={Array.isArray(formData.taggingIds) ? formData.taggingIds : []}
                        onChange={handleCompanySelectChange}
                        options={taggingOptions}
                        placeholder="Select tagging(s)"
                        label=""
                        required={false}
                        error={errors.taggingIds}
                        valueField="id"
                        labelField="name"
                        searchField="name"
                        multiple={true}
                      />
                    ) : (displayTagging || 'N/A')}
                  </div>
                </div>

                {/* Date Added – read-only */}
                <div className="datagrid-item">
                  <div className="datagrid-title">Date Added</div>
                  <div className="datagrid-content">
                    {clientData.createdAt ? (() => {
                      const d = new Date(clientData.createdAt);
                      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                    })() : 'N/A'}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Address information card */}
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="card-title">Address Information</h3>
            </div>
            <div className="card-body">
              <div className="datagrid">

                <div className="datagrid-item">
                  <div className="datagrid-title">Address</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <textarea
                        className="form-control"
                        name="address"
                        value={formData.address || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter address"
                        rows="3"
                        autoComplete="street-address"
                      />
                    ) : (clientData.address || 'N/A')}
                  </div>
                </div>

                <div className="datagrid-item">
                  <div className="datagrid-title">Country</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-control"
                        name="country"
                        value={formData.country || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter country"
                        autoComplete="country"
                      />
                    ) : (clientData.country || 'N/A')}
                  </div>
                </div>

                <div className="datagrid-item">
                  <div className="datagrid-title">State</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-control"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter state"
                        autoComplete="address-level1"
                      />
                    ) : (clientData.state || 'N/A')}
                  </div>
                </div>

                <div className="datagrid-item">
                  <div className="datagrid-title">City</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-control"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter city"
                        autoComplete="address-level2"
                      />
                    ) : (clientData.city || 'N/A')}
                  </div>
                </div>

                <div className="datagrid-item">
                  <div className="datagrid-title">Postal Code</div>
                  <div className="datagrid-content">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-control"
                        name="postalCode"
                        value={formData.postalCode || ''}
                        onChange={handleCompanyInputChange}
                        placeholder="Enter postal code"
                        autoComplete="postal-code"
                      />
                    ) : (clientData.postalCode || 'N/A')}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Contacts card — editable in the same edit session as the company
            fields above, since both are saved by one PUT /md/companies/{id} */}
        <div className="col-lg-12">
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="card-title me-auto">
                Contacts
                {isEditing && editableContacts.length > 0 && (
                  <span className="badge bg-blue-lt ms-2">{editableContacts.length}</span>
                )}
              </h3>
              {isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-primary ms-auto"
                  onClick={handleAddContact}
                  disabled={isLoading}
                >
                  <svg className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Add Contact
                </button>
              )}
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  {/* The read-only list comes from /crm/contacts/by-company,
                      which returns a single combined `name`; the edit form works
                      on first/last name separately, so the header differs. */}
                  {isEditing ? (
                    <tr>
                      <th>First Name<span className="text-danger"> *</span></th>
                      <th>Last Name</th>
                      <th>Position</th>
                      <th>Email<span className="text-danger"> *</span></th>
                      <th>Phone</th>
                      <th className="w-1"></th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Email</th>
                      <th>Phone</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {isEditing ? (
                    editableContacts.length > 0 ? editableContacts.map((contact, index) => {
                      const rowErrors = errors.contacts?.[index] || {};
                      return (
                        <tr
                          key={contact.id ?? `new-${index}`}
                          className={contact.__isNew ? 'bg-primary-lt' : undefined}
                        >
                          <td style={{ minWidth: '170px' }}>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                ref={(el) => { contactNameInputRefs.current[index] = el; }}
                                type="text"
                                className={`form-control ${rowErrors.firstName ? 'is-invalid' : ''}`}
                                value={contact.firstName || ''}
                                onChange={(e) => handleContactChange(index, 'firstName', e.target.value)}
                                placeholder="First name"
                                autoComplete="off"
                              />
                              {contact.__isNew && (
                                <span className="badge bg-primary text-white flex-shrink-0">New</span>
                              )}
                            </div>
                            {rowErrors.firstName && (
                              <div className="invalid-feedback d-block">{rowErrors.firstName}</div>
                            )}
                          </td>
                          <td style={{ minWidth: '150px' }}>
                            <input
                              type="text"
                              className="form-control"
                              value={contact.lastName || ''}
                              onChange={(e) => handleContactChange(index, 'lastName', e.target.value)}
                              placeholder="Last name"
                              autoComplete="off"
                            />
                          </td>
                          <td style={{ minWidth: '150px' }}>
                            <input
                              type="text"
                              className="form-control"
                              value={contact.position || ''}
                              onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                              placeholder="Position"
                              autoComplete="off"
                            />
                          </td>
                          <td style={{ minWidth: '220px' }}>
                            <input
                              type="email"
                              className={`form-control ${rowErrors.email ? 'is-invalid' : ''}`}
                              value={contact.email || ''}
                              onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                              placeholder="name@company.com"
                              autoComplete="off"
                            />
                            {rowErrors.email && <div className="invalid-feedback">{rowErrors.email}</div>}
                          </td>
                          <td style={{ minWidth: '150px' }}>
                            <input
                              type="tel"
                              className="form-control"
                              value={contact.phone || ''}
                              onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                              placeholder="Phone"
                              inputMode="tel"
                              autoComplete="off"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-icon btn-outline-danger"
                              onClick={() => handleRemoveContact(index)}
                              disabled={isLoading}
                              title="Remove contact"
                              aria-label="Remove contact"
                            >
                              <svg className="icon" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <line x1="4" y1="7" x2="20" y2="7"></line>
                                <path d="M10 11v6"></path>
                                <path d="M14 11v6"></path>
                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="text-secondary mb-2">No contacts yet.</div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleAddContact}
                            disabled={isLoading}
                          >
                            Add the first contact
                          </button>
                        </td>
                      </tr>
                    )
                  ) : contacts.length > 0 ? contacts.map((contact, index) => (
                    <tr key={contact.id || index}>
                      <td>{contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'N/A'}</td>
                      <td className="text-secondary">{contact.position || contact.title || contact.jobTitle || contact.role || 'N/A'}</td>
                      <td className="text-secondary">
                        {contact.email
                          ? <a href={`mailto:${contact.email}`} className="text-reset">{contact.email}</a>
                          : 'N/A'}
                      </td>
                      <td className="text-secondary">{contact.phone || contact.phoneNumber || contact.contactNumber || 'N/A'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="text-center text-secondary py-4">
                        No contacts found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {isEditing && (
              <div className="card-footer text-secondary small">
                Contact changes are applied when you press <strong>Save</strong> in the
                header. Removing a contact here deletes it from the company on save.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewView;
